export const normalizeHomeShortcutPreference = (payload) => {
  if (!payload?.customized || !Array.isArray(payload.shortcut_keys)) {
    return null;
  }

  return Array.from(
    new Set(payload.shortcut_keys.filter((key) => typeof key === "string")),
  );
};

const HOME_INTERFACE_GROUP_COLORS = Object.freeze({
  "gestion-commerciale": "#295d86",
  admin: "#54657a",
  "ressources-humaines": "#7c3aed",
  "menu-global": "#0f8ea8",
  logistics: "#2f9d62",
  hotellerie: "#c58a00",
});

const normalizeShortcutPath = (value) => {
  const path = String(value || "").trim();
  if (!path.startsWith("/")) return "";
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
};

const normalizeShortcutKeyPart = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9._:-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72);

export const buildHomeInterfaceShortcuts = (
  menuTreeData,
  { reservedPaths = [] } = {},
) => {
  if (!Array.isArray(menuTreeData)) return [];

  const usedPaths = new Set(
    reservedPaths.map(normalizeShortcutPath).filter(Boolean),
  );
  const usedKeys = new Set();
  const shortcuts = [];

  const visitNode = (
    node,
    { rootKey, rootTitle, inheritedIcon = null, ancestors = [] },
  ) => {
    if (!node || typeof node !== "object") return;

    const children = Array.isArray(node.children) ? node.children : [];
    const icon = node.icon || inheritedIcon;
    const path = normalizeShortcutPath(node.path);
    const isNavigableLeaf = Boolean(path) && children.length === 0;

    if (isNavigableLeaf && path !== "/accueil" && !usedPaths.has(path)) {
      const leafKey = normalizeShortcutKeyPart(node.key || path);
      const normalizedRootKey = normalizeShortcutKeyPart(rootKey || "autres");
      const key = `interface:${normalizedRootKey}:${leafKey}`;

      if (leafKey && !usedKeys.has(key)) {
        usedKeys.add(key);
        usedPaths.add(path);
        shortcuts.push({
          key,
          title: String(node.title || "Interface"),
          path,
          icon,
          color: HOME_INTERFACE_GROUP_COLORS[rootKey] || "#295d86",
          groupKey: normalizedRootKey,
          groupTitle: String(rootTitle || "Autres interfaces"),
          contextLabel: ancestors.map((ancestor) => ancestor.title).join(" · "),
          kind: "interface",
        });
      }
    }

    children.forEach((child) =>
      visitNode(child, {
        rootKey,
        rootTitle,
        inheritedIcon: icon,
        ancestors: [
          ...ancestors,
          ...(node.key === rootKey
            ? []
            : [{ key: node.key, title: String(node.title || "") }]),
        ],
      }),
    );
  };

  menuTreeData.forEach((rootNode) => {
    if (!rootNode || typeof rootNode !== "object") return;

    visitNode(rootNode, {
      rootKey: String(rootNode.key || "autres"),
      rootTitle: String(rootNode.title || "Autres interfaces"),
      inheritedIcon: rootNode.icon || null,
      ancestors: [],
    });
  });

  return shortcuts;
};

export const filterHomeShortcuts = (
  availableShortcuts,
  preferenceKeys,
  defaultKeys = null,
) => {
  const effectiveKeys = Array.isArray(preferenceKeys)
    ? preferenceKeys
    : defaultKeys;

  if (!Array.isArray(effectiveKeys)) return availableShortcuts;

  const selectedKeys = new Set(effectiveKeys);
  return availableShortcuts.filter((shortcut) => selectedKeys.has(shortcut.key));
};

export const mergeHomeShortcutSelection = ({
  availableShortcuts,
  preferenceKeys,
  selectedKeys,
}) => {
  const availableKeys = new Set(
    availableShortcuts.map((shortcut) => shortcut.key),
  );
  const selectedKeySet = new Set(selectedKeys);
  const unavailablePersistedKeys = Array.isArray(preferenceKeys)
    ? preferenceKeys.filter((key) => !availableKeys.has(key))
    : [];
  const selectedAvailableKeys = availableShortcuts
    .map((shortcut) => shortcut.key)
    .filter((key) => selectedKeySet.has(key));

  return Array.from(
    new Set([...unavailablePersistedKeys, ...selectedAvailableKeys]),
  );
};
